package com.dotdrives.web.pages.forms

import com.dotdrives.controller.FormBuilderUiState
import com.dotdrives.controller.PublicGivingController
import com.dotdrives.controller.donorPreview
import com.dotdrives.model.PublicGivingForm
import com.dotdrives.web.pages.giving.PublicGivingPage
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.button
import web.html.HTMLDivElement

external interface FormDonorPreviewProps : Props {
    var state: FormBuilderUiState
    var orgName: String
    var editing: Boolean?
    var onEditSection: ((String) -> Unit)?
}

/** Draft changes replace only the isolated donor session; device changes preserve test input. */
val FormDonorPreview = FC<FormDonorPreviewProps> { props ->
    val editing = props.editing != false
    val (device, setDevice) = useState("mobile")
    val (available, setAvailable) = useState(390.0)
    val stage = useRef<HTMLDivElement>(null)
    useEffect(editing) { setDevice(if (editing) "mobile" else "desktop") }
    useEffect(Unit) {
        val element = stage.current
        if (element != null) {
            val callback: (dynamic) -> Unit = { entries ->
                val width = entries[0].contentRect.width as Double
                setAvailable(width.coerceAtLeast(1.0))
            }
            val observer = js("new ResizeObserver(callback)")
            observer.observe(element)
            try { awaitCancellation() } finally { observer.disconnect() }
        }
    }
    val form = props.state.donorPreview(props.orgName) ?: return@FC
    val scale = if (device == "desktop") minOf(if (editing) .62 else .86, available / 1100.0) else minOf(1.0, available / 390.0)
    FormDonorPreviewStyles()
    div {
        className = ClassName("forms-donor-preview v2-donor-preview")
        div {
            className = ClassName("v2-preview-toolbar")
            div {
                className = ClassName("v2-device-switch")
                listOf("mobile" to "Phone", "desktop" to "Desktop").forEach { (value, title) ->
                    button {
                        type = web.html.ButtonType.button
                        className = ClassName(if (device == value) "selected" else "")
                        ariaPressed = (device == value).unsafeCast<react.dom.aria.AriaPressed>()
                        onClick = { setDevice(value) }
                        +title
                    }
                }
            }
            span { className = ClassName("v2-preview-hint"); +"Interactive preview · Payments disabled" }
        }
        div {
            className = ClassName("v2-preview-stage")
            ref = stage
            div {
                className = ClassName("v2-preview-sizing")
                asDynamic().style = js("({})")
                asDynamic().style.width = "${(if (device == "desktop") 1100 else 390) * scale}px"
                // CSS zoom lays out the complete phone height, avoiding clipped long forms.
                div {
                    className = ClassName("v2-preview-frame v2-preview-$device")
                    asDynamic().style = js("({})")
                    asDynamic().style.zoom = scale
                    onClick = { event ->
                        val target = event.target.asDynamic()
                        if (target.closest != null && target.closest("button,input,select,textarea,a,label") == null) {
                            val section = when {
                                target.closest(".give-story") != null -> "story"
                                target.closest("fieldset")?.querySelector(".give-amounts") != null -> "amounts"
                                target.closest("fieldset")?.querySelector(".give-contact-grid") != null -> "fields"
                                else -> null
                            }
                            section?.let { props.onEditSection?.invoke(it) }
                        }
                    }
                    if (editing && props.onEditSection != null) {
                        div {
                            className = ClassName("v2-preview-jumps")
                            listOf("story" to "Edit story", "amounts" to "Edit gifts", "fields" to "Edit details").forEach { (section, title) ->
                                button { type = web.html.ButtonType.button; onClick = { props.onEditSection?.invoke(section) }; +title }
                            }
                        }
                    }
                    IsolatedDonorPreview {
                        key = form.toString().unsafeCast<Key>()
                        this.form = form
                    }
                }
            }
        }
        div {
            className = ClassName("forms-donor-preview-note v2-preview-note")
            +"Uses your current editor values. Nothing entered here is submitted or saved. Payment fees and campaign progress are not shown."
        }
    }
}

private external interface IsolatedDonorPreviewProps : Props { var form: PublicGivingForm }
private val IsolatedDonorPreview = FC<IsolatedDonorPreviewProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        scope to PublicGivingController.preview(props.form, scope)
    }
    val preview = useFlowState(holder.second.state)
    useEffect(holder) { try { awaitCancellation() } finally { holder.first.cancel() } }
    PublicGivingPage {
        state = preview; controller = holder.second; paymentReturn = null; missingReturn = false; onNewGift = {}
    }
}
